import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Copy, Check, Key } from "lucide-react";
import MarketingNav from "../components/MarketingNav";
import Button from "../components/atoms/Button";

const BASE = "https://speakeasy.k2techinfo.com";

const SIDE_GROUPS = [
  { title: "Get started", links: [
    { id: "quickstart",  label: "Quickstart" },
    { id: "auth",        label: "Authentication" },
    { id: "rate-limits", label: "Rate limits" },
  ]},
  { title: "Endpoints", links: [
    { id: "grammar",     label: "POST /v1/grammar-check" },
    { id: "dictionary",  label: "GET /v1/dictionary/:word" },
    { id: "conversation",label: "POST /v1/conversation" },
    { id: "word-of-day", label: "GET /v1/word-of-day" },
  ]},
  { title: "Resources", links: [
    { id: "pricing",     label: "Pricing" },
    { id: "errors",      label: "Errors" },
  ]},
];

export default function ApiDocs() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const { hash } = useLocation();
  const active = (hash || "#quickstart").slice(1);

  async function handleSignup(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { setError("Name and email required"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch("/api/developer/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email }),
      });
      const data = await res.json();
      if (data.key) setApiKey(data.key);
      else setError(data.error || data.message || "Something went wrong");
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  }

  function copyKey() {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div style={{ background: "var(--ink-900)", color: "var(--paper)", minHeight: "100vh" }}>
      <MarketingNav dark />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ padding: "32px 24px 96px", display: "grid", gridTemplateColumns: "220px 1fr 280px", gap: 32 }}>

        {/* Sidebar */}
        <aside className="hidden lg:block" style={{ paddingTop: 12 }}>
          {SIDE_GROUPS.map((g) => (
            <div key={g.title}>
              <p style={groupH}>{g.title}</p>
              {g.links.map((l) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  style={{
                    display: "block",
                    padding: active === l.id ? "6px 10px" : "6px 12px",
                    borderRadius: 6,
                    fontSize: 13,
                    color: active === l.id ? "var(--gold-300)" : "rgba(251,246,232,0.70)",
                    background: active === l.id ? "rgba(200,154,44,0.18)" : "transparent",
                    borderLeft: active === l.id ? "2px solid var(--gold-500)" : "2px solid transparent",
                    marginBottom: 2,
                    textDecoration: "none",
                  }}
                >
                  {l.label}
                </a>
              ))}
            </div>
          ))}
        </aside>

        {/* Main */}
        <main style={{ maxWidth: 720 }}>
          <span className="eyebrow" style={{ color: "var(--gold-300)" }}>Speakeasy API · v1</span>
          <h1 className="font-display" id="quickstart" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, margin: "12px 0 16px", letterSpacing: "-0.025em" }}>
            Score speech in <em style={{ color: "var(--gold-300)" }}>under 200ms.</em>
          </h1>
          <p style={p}>
            The same engine the SpeakEasy app uses. Send an audio clip and the target sentence; get back a band score, a word-level breakdown, and the syllables your speaker stressed wrong. Built for ed-tech, telephony, and accessibility.
          </p>

          {/* API key signup */}
          <div id="auth" style={{ background: "rgba(251,246,232,0.04)", border: "1px solid rgba(251,246,232,0.10)", borderRadius: "var(--radius-lg)", padding: 28, marginTop: 32 }}>
            <h2 className="font-display" style={h2}>
              Get a <em style={{ color: "var(--gold-300)" }}>free</em> key.
            </h2>
            <p style={{ ...p, fontSize: 14, marginBottom: 18 }}>1,000 calls a month, free. No card.</p>
            {apiKey ? (
              <div>
                <div style={{ background: "#050913", border: "1px solid rgba(251,246,232,0.10)", borderRadius: 10, padding: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <code style={{ color: "var(--gold-300)", fontFamily: "var(--font-mono)", fontSize: 13, wordBreak: "break-all" }}>{apiKey}</code>
                  <button type="button" onClick={copyKey} style={{ flexShrink: 0, padding: 8, background: "transparent", color: "rgba(251,246,232,0.65)", border: 0, cursor: "pointer" }} aria-label="Copy key">
                    {copied ? <Check size={18} color="var(--sage)" /> : <Copy size={18} />}
                  </button>
                </div>
                <p style={{ ...p, fontSize: 13, marginTop: 12 }}>
                  Save this key — you won't see it again. Pass it as <code style={inlineCode}>X-API-Key</code> on every request.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSignup} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {error && <div style={{ background: "rgba(184,69,78,0.20)", border: "1px solid rgba(184,69,78,0.40)", color: "var(--berry-light)", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>{error}</div>}
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" style={input} />
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" type="email" style={input} />
                <Button type="submit" variant="primary" disabled={loading} icon={<Key size={16} strokeWidth={1.8} />}>
                  {loading ? "Creating…" : "Get a free key"}
                </Button>
              </form>
            )}
          </div>

          {/* Quickstart */}
          <h2 className="font-display" style={h2}>The <em style={{ color: "var(--gold-300)" }}>simplest</em> example.</h2>
          <p style={p}>Send a wav, give us the sentence the speaker was reading, get a score. Nothing else required.</p>
          <Code>{`# Score a clip against a target sentence
curl -X POST ${BASE}/api/v1/speech/score \\
  -H "Authorization: Bearer $SPEAKEASY_KEY" \\
  -F "audio=@hello.wav" \\
  -F "target=The cafe adjacent to the bookstore."`}</Code>

          <h2 className="font-display" style={h2}>What you get <em style={{ color: "var(--gold-300)" }}>back.</em></h2>
          <p style={p}>Sage means clean. Gold means almost. Berry means missed.</p>
          <Code>{`{
  "score": 87,
  "band": "sage",
  "latency_ms": 182,
  "words": [
    { "word": "The",      "score": 98, "band": "sage" },
    { "word": "cafe",     "score": 94, "band": "sage" },
    { "word": "adjacent", "score": 71, "band": "gold",
      "hint": "stress on the second syllable: ad-JA-cent" },
    { "word": "to",        "score": 96, "band": "sage" },
    { "word": "the",       "score": 93, "band": "sage" },
    { "word": "bookstore", "score": 82, "band": "gold" }
  ]
}`}</Code>

          {/* Endpoints */}
          <h2 className="font-display" id="grammar" style={h2}>Endpoints.</h2>

          <Endpoint method="POST" path="/api/v1/grammar-check" desc="Check grammar with 200+ rules across 21 categories.">
            <Code>{`curl -X POST ${BASE}/api/v1/grammar-check \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_KEY" \\
  -d '{"sentence": "She don\\'t likes the movie."}'

# Response
{
  "score": 55,
  "errors": [
    {"type": "subject-verb", "message": "Use doesn't with she"},
    {"type": "modal", "message": "Base form after doesn't"}
  ],
  "rulesChecked": 200
}`}</Code>
          </Endpoint>

          <Endpoint id="dictionary" method="GET" path="/api/v1/dictionary/:word" desc="Look up any of 102,265 English words.">
            <Code>{`curl ${BASE}/api/v1/dictionary/accomplish \\
  -H "X-API-Key: YOUR_KEY"

# Response
{
  "word": "accomplish",
  "phonetic": "/əˈkɒm.plɪʃ/",
  "pos": "verb",
  "def": "to achieve or complete successfully"
}`}</Code>
          </Endpoint>

          <Endpoint id="conversation" method="POST" path="/api/v1/conversation" desc="AI conversation practice across 20 real-life scenarios.">
            <Code>{`curl -X POST ${BASE}/api/v1/conversation \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_KEY" \\
  -d '{"scenario": "interview", "message": "I have 5 years experience"}'`}</Code>
          </Endpoint>

          <Endpoint id="word-of-day" method="GET" path="/api/v1/word-of-day" desc="Daily vocabulary word with pronunciation and example.">
            <Code>{`curl ${BASE}/api/v1/word-of-day -H "X-API-Key: YOUR_KEY"`}</Code>
          </Endpoint>

          {/* Pricing */}
          <h2 className="font-display" id="pricing" style={h2}>Pricing, <em style={{ color: "var(--gold-300)" }}>plain.</em></h2>
          <p style={p}>Pay per call. No minimum. No "talk to sales".</p>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 14, marginBottom: 22 }}>
            <thead>
              <tr>
                <th style={apiTh}>Endpoint</th>
                <th style={apiTh}>Price</th>
                <th style={apiTh}>Free monthly</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["/v1/speech/score",       "₹0.40 / call", "1,000 calls"],
                ["/v1/speech/transcribe",  "₹0.20 / call", "1,000 calls"],
                ["/v1/dictionary/lookup",  "free",          "unlimited"],
                ["/v1/grammar/check",      "₹0.10 / call", "5,000 calls"],
                ["/v1/conversation",       "₹0.80 / call", "500 calls"],
              ].map(([endpoint, price, free]) => (
                <tr key={endpoint}>
                  <td style={apiTd}>{endpoint}</td>
                  <td style={apiTd}>{price}</td>
                  <td style={apiTd}>{free}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </main>

        {/* Right rail */}
        <aside className="hidden xl:block" style={{ paddingTop: 12 }}>
          <Tip h="A quick note.">
            Audio sent to <code style={inlineCode}>/score</code> is processed in-region (Mumbai by default) and discarded after the response. We don't keep it. We don't train on it.
          </Tip>
          <Tip h="SDKs.">
            Official: Node, Python, Go, PHP. Community: Ruby, Rust, Elixir.
          </Tip>
          <Tip h="Status.">
            All systems sage. Last incident: 11 days ago.
          </Tip>
        </aside>
      </div>
    </div>
  );
}

function Endpoint({ id, method, path, desc, children }) {
  const colors = { POST: "var(--rust)", GET: "var(--sage)", PUT: "var(--gold-500)", DELETE: "var(--berry)" };
  const textOnChip = method === "GET" || method === "PUT" ? "var(--ink-900)" : "var(--paper)";
  return (
    <div id={id} style={{ marginTop: 22 }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "rgba(251,246,232,0.04)", border: "1px solid rgba(251,246,232,0.10)", borderRadius: 8, fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--paper)" }}>
        <span style={{ display: "inline-block", padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", background: colors[method], color: textOnChip }}>{method}</span>
        <span>{BASE}{path}</span>
      </div>
      <p style={{ ...p, marginTop: 10 }}>{desc}</p>
      {children}
    </div>
  );
}

function Code({ children }) {
  return (
    <pre style={{
      background: "#050913",
      border: "1px solid rgba(251,246,232,0.10)",
      borderRadius: 10, padding: 18,
      fontFamily: "var(--font-mono)", fontSize: 13,
      color: "var(--paper)", lineHeight: 1.65,
      overflowX: "auto",
      margin: "14px 0 22px",
    }}>{children}</pre>
  );
}

function Tip({ h, children }) {
  return (
    <div style={{ padding: 16, background: "rgba(200,154,44,0.10)", border: "1px solid rgba(200,154,44,0.30)", borderRadius: 12, marginBottom: 14 }}>
      <div className="font-display" style={{ fontStyle: "italic", fontSize: 18, color: "var(--gold-300)", margin: "0 0 6px" }}>{h}</div>
      <p style={{ fontSize: 13, color: "rgba(251,246,232,0.78)", margin: 0, lineHeight: 1.55 }}>{children}</p>
    </div>
  );
}

const groupH = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "rgba(251,246,232,0.45)", margin: "20px 0 8px" };
const h2 = { fontSize: 30, fontWeight: 400, margin: "48px 0 12px", lineHeight: 1.15, color: "var(--paper)" };
const p  = { color: "rgba(251,246,232,0.78)", fontSize: 15, lineHeight: 1.65, margin: "0 0 18px" };
const inlineCode = { background: "rgba(251,246,232,0.06)", color: "var(--gold-300)", padding: "1px 6px", borderRadius: 4, fontFamily: "var(--font-mono)", fontSize: "0.9em" };
const input = { background: "#050913", border: "1px solid rgba(251,246,232,0.20)", color: "var(--paper)", borderRadius: 8, padding: "12px 14px", fontSize: 14, fontFamily: "inherit", outline: "none" };
const apiTh = { textAlign: "left", padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "var(--paper)", borderBottom: "1px solid rgba(251,246,232,0.10)" };
const apiTd = { textAlign: "left", padding: "10px 14px", fontSize: 13, color: "rgba(251,246,232,0.78)", borderBottom: "1px solid rgba(251,246,232,0.10)", fontFamily: "var(--font-mono)" };
